import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { Language } from "@/lib/translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const getInitialLanguage = (): Language => {
    // During server rendering, we must derive language strictly from the URL path.
    // If we use 'en' as default on server and user is on /pl, we get hydration mismatch.
    const path = typeof window === "undefined" ? location.pathname : window.location.pathname;
    
    if (path.startsWith("/pl")) return "pl";
    if (path.startsWith("/by")) return "by";
    
    // Client-side fallback to localStorage only if path is neutral (EN)
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("language") as Language;
      if (saved === "pl" || saved === "by") return saved;
    }
    
    return "en";
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    const isPolishPath = location.pathname.startsWith("/pl");
    const isBelarusianPath = location.pathname.startsWith("/by");
    if (isPolishPath && language !== "pl") {
      setLanguageState("pl");
    } else if (isBelarusianPath && language !== "by") {
      setLanguageState("by");
    } else if (!isPolishPath && !isBelarusianPath && language !== "en") {
      setLanguageState("en");
    }
  }, [location.pathname]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
    
    const currentPath = location.pathname;
    const hash = location.hash;
    
    // Strip existing language prefix
    let basePath = currentPath;
    if (basePath.startsWith("/pl")) {
      basePath = basePath === "/pl" ? "/" : basePath.replace(/^\/pl/, "");
    } else if (basePath.startsWith("/by")) {
      basePath = basePath === "/by" ? "/" : basePath.replace(/^\/by/, "");
    }
    
    if (lang === "en") {
      navigate(basePath + hash);
    } else {
      const newPath = basePath === "/" ? `/${lang}` : `/${lang}${basePath}`;
      navigate(newPath + hash);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
